/**
 * Guides - Documentation and help guides
 */

import React, { useState } from 'react'
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  Paper
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Search as SearchIcon,
  Book as BookIcon,
  Warning as WarningIcon,
  Build as BuildIcon,
  Folder as FolderIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material'

interface Guide {
  id: string
  title: string
  category: string
  icon: React.ReactElement
  content: {
    overview: string
    steps?: string[]
    tips?: string[]
    relatedTopics?: string[]
  }
}

const guides: Guide[] = [
  {
    id: 'getting-started',
    title: 'Getting Started with Inspector',
    category: 'Basics',
    icon: <BookIcon />,
    content: {
      overview: 'Learn how to navigate and use the Inspector tool effectively.',
      steps: [
        'Log in using your provided credentials',
        'Navigate to the Dashboard to see an overview of your data',
        'Check the Exceptions tab for any issues that need attention',
        'Use the Tools section to run reports and diagnostics',
        'Explore your data using the Data Explorer'
      ],
      tips: [
        'Start by reviewing any critical exceptions',
        'Set up regular sync schedules for optimal data freshness',
        'Use filters to focus on specific data subsets'
      ],
      relatedTopics: ['Navigation', 'Dashboard Overview', 'User Permissions']
    }
  },
  {
    id: 'understanding-exceptions',
    title: 'Understanding Data Exceptions',
    category: 'Exceptions',
    icon: <WarningIcon />,
    content: {
      overview: 'Learn what data exceptions mean and how to resolve them.',
      steps: [
        'Navigate to the Exceptions section',
        'Review the exception severity levels (Critical, Warning, Info)',
        'Click on an exception to see detailed information',
        'Follow the suggested resolution steps',
        'Mark exceptions as resolved once addressed'
      ],
      tips: [
        'Address critical exceptions first',
        'Some exceptions may resolve automatically with the next sync',
        'Contact support if you need help resolving complex exceptions'
      ],
      relatedTopics: ['Exception Types', 'Sync Issues', 'Data Quality']
    }
  },
  {
    id: 'using-tools',
    title: 'Using Inspector Tools',
    category: 'Tools',
    icon: <BuildIcon />,
    content: {
      overview: 'Discover the powerful tools available for data analysis and reporting.',
      steps: [
        'Navigate to the Tools section',
        'Choose the specific tool you need from the catalog',
        'Configure any required parameters',
        'Run the tool and review the results'
      ],
      tips: [
        'COGS Review tool helps identify pricing issues',
        'Export tools allow you to download data for external analysis',
        'Diagnostic tools help troubleshoot sync issues'
      ],
      relatedTopics: ['COGS Analysis', 'Data Export', 'System Diagnostics']
    }
  },
  {
    id: 'data-explorer-guide',
    title: 'Navigating the Data Explorer',
    category: 'Data',
    icon: <FolderIcon />,
    content: {
      overview: 'Learn how to browse and search your synchronized data.',
      steps: [
        'Go to the Data Explorer section',
        'Select the data type you want to view (Products, Sales, Clients)',
        'Use the search bar to find specific records',
        'Apply filters to narrow down results',
        'Export data for external analysis if needed'
      ],
      tips: [
        'Use date range filters for time-based analysis',
        'Export functionality preserves all filters',
        'Sync status shows when data was last updated'
      ],
      relatedTopics: ['Data Types', 'Search Functionality', 'Export Options']
    }
  },
  {
    id: 'improving-data-quality',
    title: 'Improving Data Quality',
    category: 'Best Practices',
    icon: <TrendingUpIcon />,
    content: {
      overview: 'Best practices for maintaining high-quality data across systems.',
      steps: [
        'Regularly review and resolve exceptions',
        'Ensure product codes are consistent across systems',
        'Keep client information up to date',
        'Verify pricing and cost information',
        'Monitor sync status for all systems'
      ],
      tips: [
        'Schedule regular data audits',
        'Train staff on proper data entry',
        'Use validation rules where possible',
        'Document any manual corrections'
      ],
      relatedTopics: ['Data Validation', 'Sync Frequency', 'Staff Training']
    }
  }
]

const Guides: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('')
  const [expandedPanel, setExpandedPanel] = useState<string | false>(false)

  const handleAccordionChange = (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpandedPanel(isExpanded ? panel : false)
  }

  const filteredGuides = guides.filter(guide =>
    guide.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    guide.content.overview.toLowerCase().includes(searchQuery.toLowerCase()) ||
    guide.category.toLowerCase().includes(searchQuery.toLowerCase())
  )

  const getCategoryColor = (category: string) => {
    const colors: Record<string, 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success'> = {
      'Basics': 'primary',
      'Exceptions': 'error',
      'Tools': 'info',
      'Data': 'secondary',
      'Best Practices': 'success'
    }
    return colors[category] || 'default'
  }

  return (
    <Box>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Help Guides & Documentation
      </Typography>
      
      <TextField
        fullWidth
        placeholder="Search guides..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
      />

      {filteredGuides.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No guides found matching your search.
          </Typography>
        </Paper>
      ) : (
        filteredGuides.map((guide) => (
          <Accordion
            key={guide.id}
            expanded={expandedPanel === guide.id}
            onChange={handleAccordionChange(guide.id)}
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                {guide.icon}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={500}>
                    {guide.title}
                  </Typography>
                </Box>
                <Chip
                  label={guide.category}
                  size="small"
                  color={getCategoryColor(guide.category)}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                <Typography variant="body2" paragraph>
                  {guide.content.overview}
                </Typography>

                {guide.content.steps && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Steps:
                    </Typography>
                    <List dense>
                      {guide.content.steps.map((step, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`${index + 1}. ${step}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {guide.content.tips && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Tips:
                    </Typography>
                    <List dense>
                      {guide.content.tips.map((tip, index) => (
                        <ListItem key={index}>
                          <ListItemText
                            primary={`• ${tip}`}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {guide.content.relatedTopics && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                      Related Topics:
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {guide.content.relatedTopics.map((topic, index) => (
                        <Chip
                          key={index}
                          label={topic}
                          size="small"
                          variant="outlined"
                          sx={{ mb: 1 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))
      )}
    </Box>
  )
}

export default Guides